import React, { useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "iuvity-ui-components/dist/button";
import { NewCustomDialog, NewDialogProps } from "../../components/dialog/NewCustomDialog";
import { ProductsFormSchema } from "../../validations/schemas";
import { REVALIDATE_FORM_PROPS, infoProductsFields } from "../../constants/index";
import { AppContext } from "../../context";
import { useProducts } from "../../hooks/useProducts";
import { Header } from "../../components";
import { BoxFull, TransactionalSubheader } from "iuvity-ui-components";
import { useNavigate } from "react-router-dom";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { useVersions } from "../../hooks/useVersions";
import { useTranslations } from "../../hooks/useTranslations";
import { createLabelButtonMaker } from "../../helpers/createLabelButtonMaker";

interface NewProductProps {
  update?: boolean;
}

const productDefaultValues = {
  name: "",
  nameinapi: "",
  labelkey: "",
  hastransactions: false,
  releaseversion: "",
};

export const NewProductPage: React.FC<NewProductProps> = ({ update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user },
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: productDefaultValues,
  });
  const {
    getValues,
    setValue,
    reset,
    formState: { isDirty, isValid },
  } = form;
  
  const { productAction } = useProducts("ALL");
  const { data: versionsData } = useVersions("DATA");
  const { data: labelsData, reloadFn } = useTranslations("DATA");
  const [modalProps, setModalProps] = useState<NewDialogProps>({
    visible: false,
  } as NewDialogProps);

  const formKeysOptions = useMemo(() => {
    if (versionsData && labelsData) {
      return {
        releaseversion: versionsData
          .map((version: any) => ({
            label: version?.name,
            value: version?.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
        labelkey: labelsData?.filter((item: any) => item?.widgets)
          ?.map((item: any)=> ({ label: item?.key, value: item?.key}))
          ?.sort((a: any, b: any) => a.label.localeCompare(b.label)),
      };
    } else {
      return;
    }
  }, [labelsData, versionsData]);

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionStatus = await productAction("CREATE", {
      ...formValues,
      createdby: user?.name,
    });
    if (actionStatus === "SUCCESS") {
      navigate(-1);
      window.scrollTo(0, 0);
    }
    reset(productDefaultValues);
  };
  
  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev: any) => ({
          ...prev,
          visible: true,
          title: t(`Products.${update ? "save" : "create"}ModalTitle`),
          content: t(`Products.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Products.cancelModalTitle"),
          content: t("Products.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Products.newProductBtnLabel")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
           id={t("Products.productAdminTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Products.productAdminTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update ? t("Products.editProductBtnLabel") : t("Products.newProductBtnLabel"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <div id="margin-fixed-bgscreen">
        <CustomFormComponent
          functionality="Products"
          fields={infoProductsFields}
          form={form}
          dropdownOptions={formKeysOptions}
          schemaOfValidation={ProductsFormSchema}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
          dropdownFilterObject={{ labelkey: true }}
          customComponent={createLabelButtonMaker({
            setModalProps,
            dialogTitle: t('ConsoleTranslations.createLabelTitle'),
            dialogDetail: t('ConsoleTranslations.createLabelDescription'),
            buttonLabel: t('ConsoleTranslations.createLabelTitle'),
            functionality:'Products',
            actionsToExecute:(key: string)=>{
              reloadFn();
              setValue("labelkey", key, REVALIDATE_FORM_PROPS)
            },
          })}
        />
      </div>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            label={t("Products.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
            className="mr-4"
            variant="p-button-link"
          />
          <Button
            label={
              update
                ? t("Products.saveBtnLabel")
                : t("Products.createBtnLabel")
            }
            variant="p-button"
            onClick={() => handleModalProps("action")}
            disabled={update ? !(isValid && isDirty) : !isValid}
          />
        </div>
      </BoxFull>

      <NewCustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
        aditionalClass="new-product-page-dialog overlay-createlabelsbutton"
        acceptLabel={modalProps.acceptLabel}
        rejectLabel={modalProps.rejectLabel}
        onReject={modalProps.onReject}
      />
    </>
  );
};
